import React, { useRef, useState, useEffect } from "react";
import Webcam from "react-webcam";
import { dataURLtoFile } from "utils";
import { compressImage } from "utils/fileUtils";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { IconButton, CircularProgress, Typography, Box } from "@mui/material";

const PhotoCaptureDialog = ({
  open,
  onClose,
  onCapture,
  tracking,
  location,
  setLocation,
  isUploading = false,
  uploadProgress = null,
  title,
  hideClose = false,
}) => {
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);

  // Get location when the dialog opens, and again whenever the step's title
  // changes — in the mandatory 2-step courier-label flow the dialog stays open
  // across both steps (only `title` changes), so this is what returns the view
  // to the live camera for the next shot instead of leaving the previous step's
  // confirm/retake screen on screen.
  useEffect(() => {
    if (open) {
      resetCapture();
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setLocation({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
            });
          },
          () => setLocation({ lat: null, lng: null }),
          { enableHighAccuracy: true }
        );
      }
    }
  }, [open, title]);

  // Reset function
  const resetCapture = () => {
    setImgSrc(null);
    setUploadedFile(null);
  };

  // Compress data URL
  const compressDataURL = (dataURL, quality = 0.7, maxWidth = 1024) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to compressed data URL
        const compressedDataURL = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataURL);
      };
      
      img.src = dataURL;
    });
  };

  // Capture photo from webcam with compression
  const capture = async () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) return;
    
    setIsCompressing(true);
    try {
      // Compress the captured image
      const compressedDataURL = await compressDataURL(imageSrc, 0.8, 1024);
      setImgSrc(compressedDataURL);
    } catch (error) {
      console.error('Error compressing image:', error);
      setImgSrc(imageSrc); // Fallback to original
    } finally {
      setIsCompressing(false);
    }
  };

  // Handle file upload with compression
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !file.type.startsWith('image/')) return;

    // Check file size (limit to 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size too large. Please select a file smaller than 10MB.');
      return;
    }

    setIsCompressing(true);
    try {
      // Compress the uploaded file
      const compressedFile = await compressImage(file, 0.8, 1024);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImgSrc(e.target.result);
      };
      reader.readAsDataURL(compressedFile);
      
      // Store the compressed file
      setUploadedFile(compressedFile);
    } catch (error) {
      console.error('Error compressing uploaded image:', error);
      // Fallback to original file
      const reader = new FileReader();
      reader.onload = (e) => {
        setImgSrc(e.target.result);
      };
      reader.readAsDataURL(file);
      setUploadedFile(file);
    } finally {
      setIsCompressing(false);
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        background: "#000",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* Loading Overlay for Upload */}
      {isUploading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1500,
          }}
        >
          <CircularProgress
            variant={uploadProgress != null ? "determinate" : "indeterminate"}
            value={uploadProgress ?? 0}
            size={80}
            thickness={4}
            sx={{ color: "#fff", mb: 2 }}
          />
          <Typography variant="h6" color="#fff" sx={{ mb: 1 }}>
            {uploadProgress != null ? `Uploading Photo... ${uploadProgress}%` : "Uploading Photo..."}
          </Typography>
          <Typography variant="body2" color="#ccc">
            Please wait while we process your photo
          </Typography>
        </Box>
      )}

      {/* Loading Overlay for Compression */}
      {isCompressing && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1400,
          }}
        >
          <CircularProgress 
            size={60} 
            thickness={4} 
            sx={{ color: "#fff", mb: 2 }} 
          />
          <Typography variant="body1" color="#fff">
            Optimizing image...
          </Typography>
        </Box>
      )}

      {!imgSrc ? (
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/png"
          style={{
            position: "absolute",
            inset: 0,
            width: "100vw",
            height: "100vh",
            objectFit: "scale-down",
          }}
          videoConstraints={{
            facingMode: "environment",
            width: { ideal: 1280 },
            height: { ideal: 720 },
          }}
        />
      ) : (
        <img
          src={imgSrc}
          alt="Captured"
          style={{
            position: "absolute",
            inset: 0,
            width: "100vw",
            height: "100vh",
            objectFit: "scale-down",
          }}
        />
      )}

      {/* Tracking-number watermark + instruction banner for the current step */}
      {(tracking || title) && !isUploading && (
        <Box
          sx={{
            position: "absolute",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "rgba(0, 0, 0, 0.65)",
            borderRadius: 2,
            px: 2,
            py: 1,
            zIndex: 1400,
            maxWidth: "90%",
          }}
        >
          {tracking && (
            <Typography
              variant="h6"
              color="#fff"
              align="center"
              sx={{ fontWeight: 700, letterSpacing: 1, wordBreak: "break-all" }}
            >
              {tracking}
            </Typography>
          )}
          {title && (
            <Typography variant="subtitle1" color="#fff" align="center">
              {title}
            </Typography>
          )}
        </Box>
      )}

      {/* Hidden file input */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleFileUpload}
        style={{ display: "none" }}
      />

      {/* Floating capture/upload/confirm/retake buttons */}
      {!isUploading && !isCompressing && (
        <div
          style={{
            position: "absolute",
            bottom: "10%",
            left: 0,
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          {!imgSrc ? (
            <>
              {/* Upload button */}
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                size="large"
                sx={{
                  backgroundColor: "#fff",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  boxShadow: 3,
                  "&:hover": { backgroundColor: "#eee" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "auto",
                  margin: "0 20px",
                }}
                aria-label="Upload Photo"
              >
                <PhotoLibraryIcon sx={{ fontSize: 48, color: "#4d4d4dff" }} />
              </IconButton>

              {/* Capture button */}
              <IconButton
                onClick={capture}
                size="large"
                sx={{
                  backgroundColor: "#fff",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  boxShadow: 3,
                  "&:hover": { backgroundColor: "#eee" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "auto",
                  margin: "0 20px",
                }}
                aria-label="Take Photo"
              >
                <CameraAltIcon sx={{ fontSize: 48, color: "#4d4d4dff" }} />
              </IconButton>
            </>
          ) : (
            <>
              <IconButton
                onClick={resetCapture}
                size="large"
                color="secondary"
                sx={{
                  margin: "0 20px",
                  backgroundColor: "#fff",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  boxShadow: 3,
                  "&:hover": { backgroundColor: "#eee" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "auto",
                }}
                aria-label="Retake"
              >
                <ReplayIcon sx={{ fontSize: 48, color: "#f50057" }} />
              </IconButton>
              <IconButton
                onClick={() => {
                  if (onCapture) {
                    let file;
                    
                    if (uploadedFile) {
                      file = uploadedFile;
                    } else {
                      file = dataURLtoFile(
                        imgSrc,
                        `photo_${Date.now()}.jpg` // Changed to .jpg for better compression
                      );
                    }
                    
                    onCapture(file);
                  }
                }}
                size="large"
                color="primary"
                sx={{
                  margin: "0 20px",
                  backgroundColor: "#fff",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  boxShadow: 3,
                  "&:hover": { backgroundColor: "#eee" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  ml: 2,
                  pointerEvents: "auto",
                }}
                aria-label="Confirm"
              >
                <CheckCircleIcon sx={{ fontSize: 48, color: "#4caf50" }} />
              </IconButton>
            </>
          )}
        </div>
      )}

      {/* Close button */}
      {!isUploading && !hideClose && (
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
            width: 30,
            height: 30,
            backgroundColor: "#fff",
            zIndex: 1400,
            pointerEvents: "auto",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          }}
        >
          <span style={{ fontSize: 32, color: "#333" }}>×</span>
        </IconButton>
      )}
    </div>
  );
};

export default PhotoCaptureDialog;